import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { AdminPerson } from '../admin/client';
import { useCellarList } from '../auth/CellarListContext';
import { useBackend } from '../backend/BackendContext';
import { DeleteCellarControl } from '../components/DeleteCellarControl';
import { TopBar } from '../components/TopBar';
import { t, tf } from '../i18n';
import { deleteCellarContents } from '../store/delete-cellar';
import { isValidEmail } from '../domain/email';
import './PeopleScreen.css';

/**
 * /admin/personnes, owner only (T067, FR-044, contracts/ui-and-emails.md): the app's users (not the
 * displayed cellar's), with whether they can sign in and an "Invité" pill until their first
 * sign-in; add a user by email; stop or restore a user's sign-in (never the owner's: their row has
 * no such control); delete a user's cellar. Reached from "Réglages". Changing an email or
 * promoting someone to owner is deliberately not offered. The route guard is in app-router.tsx;
 * the `admin` function checks the caller again on every request.
 */
export function PeopleScreen() {
  const { admin, storage } = useBackend();
  const { cellars, refresh } = useCellarList();
  const [people, setPeople] = useState<AdminPerson[] | undefined>();
  const [message, setMessage] = useState<string | undefined>();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const result = await admin.listPeople();
    if (result.ok) setPeople(result.value);
    else setMessage(result.message);
  }, [admin]);

  useEffect(() => {
    void load();
    // Fresh cellar list, so "Invité" disappears for anyone who has signed in since.
    refresh();
  }, [load, refresh]);

  /**
   * Invited but not signed in yet: their cellar and profile are only created at first sign-in,
   * so they are not in the cellar list (owner feedback, 2026-09-28). Unknown while it loads.
   */
  const isInvited = (person: AdminPerson) =>
    cellars !== undefined && !cellars.some((entry) => entry.id === person.id);

  /** Owner dark blue, user light blue, stopped user grey (owner feedback, 2026-09-28). */
  const cardClass = (person: AdminPerson) =>
    person.role === 'owner'
      ? 'people__person people__person--owner'
      : person.enabled
        ? 'people__person people__person--member'
        : 'people__person people__person--disabled';

  // Owner, 2026-10-15: said as soon as something is typed, and "Add user" stays off until fixed.
  const emailInvalid = email.trim() !== '' && !isValidEmail(email);

  async function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (!isValidEmail(email)) return;
    setBusy(true);
    setMessage(undefined);
    const result = await admin.addPerson(email.trim());
    if (result.ok) {
      setMessage(tf('admin.added', { email: result.value.email }));
      setEmail('');
      await load();
    } else {
      setMessage(result.message);
    }
    setBusy(false);
  }

  async function handleSetEnabled(person: AdminPerson, enabled: boolean) {
    setBusy(true);
    setMessage(undefined);
    const result = await admin.setEnabled(person.id, enabled);
    if (result.ok) {
      setPeople((current) => current?.map((p) => (p.id === person.id ? result.value : p)));
    } else {
      setMessage(result.message);
    }
    setBusy(false);
  }

  return (
    <>
      <TopBar />
      <main className="people">
        <h1>{t('admin.title')}</h1>
        <p>{t('admin.intro')}</p>

        <form className="people__add" onSubmit={(event) => void handleAdd(event)}>
          {/* The label sits in the field (owner, 2026-10-15), still named for screen readers; a
              "×" at its right end empties it. */}
          <label htmlFor="new-person-email" className="visually-hidden">
            {t('admin.addEmailLabel')}
          </label>
          <div className="people__field">
            <input
              id="new-person-email"
              type="email"
              required
              autoComplete="off"
              placeholder={t('admin.addEmailLabel')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {email !== '' && (
              <button
                type="button"
                className="people__clear"
                aria-label={t('admin.clearEmail')}
                onClick={() => {
                  setEmail('');
                  document.getElementById('new-person-email')?.focus();
                }}
              >
                ×
              </button>
            )}
          </div>
          {emailInvalid && (
            <p role="alert" className="people__invalid">
              {t('admin.invalidEmail')}
            </p>
          )}
          <button type="submit" disabled={busy || !isValidEmail(email)}>
            {t('admin.add')}
          </button>
        </form>

        {message && <p role="status">{message}</p>}

        {!people ? (
          <p>{t('common.loading')}</p>
        ) : (
          <ul className="people__list">
            {people.map((person) => (
              <li key={person.id} className={cardClass(person)}>
                {person.role === 'member' && (
                  // On = can sign in. One tap turns it off or on, with no confirmation (owner
                  // feedback, 2026-09-28: a switch; 2026-10-03: no "are you sure" step).
                  <button
                    type="button"
                    role="switch"
                    className="people__switch"
                    aria-checked={person.enabled}
                    aria-label={tf('admin.accessSwitch', { email: person.email })}
                    disabled={busy}
                    onClick={() => void handleSetEnabled(person, !person.enabled)}
                  >
                    <span className="people__switch-thumb" />
                  </button>
                )}
                <p className="people__email">
                  {person.email}
                  {person.role === 'member' && isInvited(person) && (
                    <span className="people__pill">{t('admin.invited')}</span>
                  )}
                </p>
                <p className="people__status">
                  {person.role === 'owner'
                    ? t('admin.owner')
                    : person.enabled
                      ? t('admin.enabled')
                      : t('admin.disabled')}
                </p>

                <DeleteCellarControl onDelete={() => deleteCellarContents(storage, person.id)} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </>
  );
}
